import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import BrandLogo from '../components/BrandLogo';
import { toast } from 'sonner';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050605] flex">
      {/* Left Side - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1604227694902-8d79311c6f19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxtaXN0eSUyMGxha2UlMjBmaXNoaW5nJTIwZGF3biUyMGRhcmslMjBtb29keXxlbnwwfHx8fDE3NzExNjg0MDZ8MA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#050605] to-transparent" />
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <BrandLogo
            variant="full"
            size="xl"
            to="/"
            className="mb-12"
            glow
            testId="login-logo"
          />

          {/* Header */}
          <div className="mb-8">
            <p className="data-label text-[#c8b273] mb-2">Welcome Back</p>
            <h1 className="font-['Barlow_Condensed'] text-4xl font-extrabold uppercase tracking-wider text-white">
              Sign In
            </h1>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="tactical-label">Email</label>
              <div className="relative">
                <Mail className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4a504a]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="tactical-input w-full pl-8"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="tactical-label">Password</label>
              <div className="relative">
                <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4a504a]" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="tactical-input w-full pl-8"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="btn-gold w-full"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </form>

          <p className="mt-4 text-center text-sm">
            <Link to="/forgot-password" className="text-[#8a948a] hover:text-[#c8b273]" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </p>

          {/* Footer */}
          <p className="mt-8 text-center text-[#8a948a]">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#c8b273] hover:underline" data-testid="register-link">
              Join Now
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
